<template>
  <!-- 首页一堆"预约/Book now"按钮点的都是这个弹窗，不是直接跳预约页——
       弹窗先收日期/时间/人数，套餐是写死的一条推荐（不能改），
       弹窗里真正的 Book now 才跳到 /appointment 那个基础信息表单页。
       Teleport 到 body：跟全屏菜单抽屉一个道理，避免被祖先的 overflow:hidden 裁掉。 -->
  <Teleport to="body">
    <transition name="fade">
      <div v-if="kanonUI.showBookingModal" class="booking-mask" @click.self="close">
        <div class="booking-panel">
          <div class="panel-header">
            <div class="title-block">
              <h2 class="title-en">APPOINTMENT</h2>
              <p class="title-cn">{{ $t("kanon.appointment.heroKimono") }}・{{ $t("kanon.appointment.heroInfo") }}</p>
            </div>
            <button class="close-btn" type="button" aria-label="close" @click="close">
              <span>×</span>
            </button>
          </div>

          <!-- 三个选择器原来是浏览器原生的 <input type=date>/<select>，样式没法自定义；
               按原型（画板5/5拷贝/5拷贝2）改成完全自己画的下拉面板：同一时间只展开一个
               （点开一个，其他自动收起），已展开的那颗字段自己的 chevron 会转向朝上 -->
          <div class="field-group">
            <label>{{ $t("kanon.bookingModal.dateLabel") }}</label>
            <div class="field toggle-field" :class="{ open: openPicker === 'date' }" @click="togglePicker('date')">
              <span class="field-display">{{ formattedDate }}</span>
              <svg class="chevron" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6" /></svg>
            </div>
            <div v-if="openPicker === 'date'" class="calendar-panel" @click.stop>
              <div class="calendar-head">
                <span class="cal-month">{{ calendarMonthLabel }}</span>
                <span class="cal-nav">
                  <button type="button" aria-label="prev month" @click="prevMonth">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 15l-6-6-6 6" /></svg>
                  </button>
                  <button type="button" aria-label="next month" @click="nextMonth">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6" /></svg>
                  </button>
                </span>
              </div>
              <div class="cal-weekdays">
                <span v-for="(w, i) in $tm('kanon.bookingModal.weekdays')" :key="i">{{ w }}</span>
              </div>
              <div class="cal-grid">
                <button
                  v-for="cell in calendarDays"
                  :key="cell.dateStr"
                  type="button"
                  class="cal-day"
                  :class="{ past: cell.isPast, selected: cell.isSelected }"
                  :disabled="cell.isPast"
                  @click="selectDay(cell)"
                >
                  {{ cell.label }}
                </button>
              </div>
            </div>
          </div>

          <div class="field-group">
            <label>{{ $t("kanon.bookingModal.timeLabel") }}</label>
            <div class="field toggle-field" :class="{ open: openPicker === 'time' }" @click="togglePicker('time')">
              <span class="field-display">{{ time }}</span>
              <svg class="chevron" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6" /></svg>
            </div>
            <div v-if="openPicker === 'time'" class="time-panel" @click.stop>
              <button
                v-for="slot in timeSlots"
                :key="slot.value"
                type="button"
                class="time-pill"
                :class="{ selected: slot.value === time }"
                @click="selectTime(slot.value)"
              >
                {{ slot.value }} <small>{{ slot.period }}</small>
              </button>
            </div>
          </div>

          <div class="field-group">
            <label>{{ $t("kanon.bookingModal.peopleLabel") }}</label>
            <div class="field toggle-field" :class="{ open: openPicker === 'people' }" @click="togglePicker('people')">
              <span class="field-display">{{ people }} {{ $t("人") }}</span>
              <svg class="chevron" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6" /></svg>
            </div>
            <div v-if="openPicker === 'people'" class="people-panel" @click.stop>
              <div
                v-for="n in 20"
                :key="n"
                class="people-item"
                :class="{ selected: n === people }"
                @click="selectPeople(n)"
              >
                {{ n }} {{ $t("人") }}
              </div>
            </div>
          </div>

          <!-- 这个套餐是写死的推荐款，不能改——原型上就是这样，选套餐这件事挪到
               弹窗之外用不着了，细节客服后续跟进 -->
          <div class="plan-box">
            <span class="plan-badge">
              <span>{{ $t("kanon.bookingModal.badgeLine1") }}</span>
              <span>{{ $t("kanon.bookingModal.badgeLine2") }}</span>
            </span>
            <div class="plan-head">
              <span class="plan-name">{{ $t("kanon.bookingModal.planName") }}</span>
              <span class="plan-price"> 4,900 <small>JPY</small></span>
            </div>
            <p class="plan-desc">{{ $t("kanon.bookingModal.planDesc") }}</p>
            <p class="plan-upsell">{{ $t("kanon.bookingModal.planUpsell") }}</p>
          </div>

          <button class="btn-book" type="button" @click="onBookNow">Book now</button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script>
import store from "@/store/store";
import { kanonUI, lockBodyScroll, unlockBodyScroll } from "@/composables/kanonUI";

// 弹窗里显示给用户看的日期（formattedDate/calendarMonthLabel）要跟着站内语言走，
// 但提交给后端的 arrivalTime（HeaderAppointment.vue 里拼的）必须一直是"YYYY年MM月DD日"
// 这个固定格式，不能跟着变——这两件事分开处理，不要混在一起改。
// 用浏览器原生 Intl.DateTimeFormat 而不是手写各语言的年/月/日模板：不同语言的
// 日期该用什么词、顺序、要不要空格，交给浏览器自己处理更准，也不用为每种语言各自维护一份格式字符串
const INTL_LOCALE_MAP = {
  zh_CN: "zh-CN",
  zh_TW: "zh-TW",
  en_US: "en-US",
  ja_JP: "ja-JP",
  ko_KR: "ko-KR",
};

export default {
  name: "KanonBookingModal",
  data() {
    const today = new Date();
    return {
      kanonUI,
      date: this.toDateStr(today),
      time: "09:00",
      people: 1,
      openPicker: null, // null | 'date' | 'time' | 'people'——同一时间只展开一个
      calendarMonth: new Date(today.getFullYear(), today.getMonth(), 1), // 日历当前翻到哪个月，跟"已选日期"是两码事
      // 原型（画板5拷贝）实测最晚档是 16:00，不是营业时间 17:30 那么晚——
      // 应该是给最后一档留够体验时长，不是笔误，照实现
      timeSlots: this.buildTimeSlots(),
    };
  },
  computed: {
    // 界面上给用户看的日期显示，跟着当前站内语言走（中文"2026年9月8日"、
    // 英文"September 8, 2026"、韩文"2026년 9월 8일"……），真正提交给后端的
    // 那份格式是 HeaderAppointment.vue 里单独拼的，跟这里显示的是两回事，不受这里影响
    formattedDate() {
      if (!this.date) return "";
      const [y, m, d] = this.date.split("-").map(Number);
      const locale = INTL_LOCALE_MAP[this.kanonUI.selectedLanguage] || "zh-CN";
      return new Intl.DateTimeFormat(locale, { year: "numeric", month: "long", day: "numeric" }).format(new Date(y, m - 1, d));
    },
    calendarMonthLabel() {
      const locale = INTL_LOCALE_MAP[this.kanonUI.selectedLanguage] || "zh-CN";
      return new Intl.DateTimeFormat(locale, { year: "numeric", month: "long" }).format(this.calendarMonth);
    },
    // 补全日历网格：上个月/下个月露出来的那几天也要显示（原型里第一行有7月尾巴、
    // 最后一行有9月开头），今天之前的日期变灰且不能点，今天及以后的正常可选
    calendarDays() {
      const year = this.calendarMonth.getFullYear();
      const month = this.calendarMonth.getMonth();
      const firstWeekday = new Date(year, month, 1).getDay();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const totalCells = Math.ceil((firstWeekday + daysInMonth) / 7) * 7;
      const todayStr = this.toDateStr(new Date());
      const cells = [];
      for (let i = 0; i < totalCells; i++) {
        const cellDate = new Date(year, month, i - firstWeekday + 1);
        const dateStr = this.toDateStr(cellDate);
        cells.push({
          label: cellDate.getDate(),
          dateStr,
          isPast: dateStr < todayStr,
          isSelected: dateStr === this.date,
        });
      }
      return cells;
    },
  },
  watch: {
    "kanonUI.showBookingModal"(isOpen) {
      if (isOpen) {
        lockBodyScroll();
      } else {
        unlockBodyScroll();
      }
    },
  },
  beforeUnmount() {
    if (this.kanonUI.showBookingModal) unlockBodyScroll();
  },
  methods: {
    pad(n) {
      return String(n).padStart(2, "0");
    },
    toDateStr(d) {
      return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}`;
    },
    buildTimeSlots() {
      // 原型上"12:00"标的是 AM，应该是原型自己标错了（12点该是 PM）——
      // 这里按真实的上下午规则算，不照抄这个小笔误
      const slots = [];
      for (let h = 9; h <= 16; h++) {
        slots.push({ value: `${this.pad(h)}:00`, period: h < 12 ? "AM" : "PM" });
        if (h < 16) slots.push({ value: `${this.pad(h)}:30`, period: h < 12 ? "AM" : "PM" });
      }
      return slots;
    },
    togglePicker(name) {
      this.openPicker = this.openPicker === name ? null : name;
      if (this.openPicker === "date") {
        // 每次重新打开日历，翻回"已选日期"所在的月份，不停留在上次翻到的月份
        const [y, m] = this.date.split("-").map(Number);
        this.calendarMonth = new Date(y, m - 1, 1);
      }
    },
    prevMonth() {
      this.calendarMonth = new Date(this.calendarMonth.getFullYear(), this.calendarMonth.getMonth() - 1, 1);
    },
    nextMonth() {
      this.calendarMonth = new Date(this.calendarMonth.getFullYear(), this.calendarMonth.getMonth() + 1, 1);
    },
    selectDay(cell) {
      if (cell.isPast) return;
      this.date = cell.dateStr;
      this.openPicker = null;
    },
    selectTime(value) {
      this.time = value;
      this.openPicker = null;
    },
    selectPeople(n) {
      this.people = n;
      this.openPicker = null;
    },
    close() {
      this.kanonUI.showBookingModal = false;
      this.openPicker = null;
    },
    onBookNow() {
      // 日期/时间/人数 + 写死的套餐信息先存一份，预约页那个表单不用重填这些，
      // 提交给后端（/dev-api/system/resOrder/add）时也是从这份数据里取套餐信息；
      // 复用老的 formData 结构（跟 store.js 里其他地方一致），没有另起一套
      store.commit("updateFormData", {
        selectedTime: `${this.date} ${this.time}`,
        num: this.people,
        planName: this.$t("kanon.bookingModal.planName"),
        // 套餐ID不在这填了——真实和服套餐ID现在统一由 HeaderAppointment.vue 里的
        // BASE_PLAN_ID（环境变量，测试72/生产78）提供，这里之前写死的占位 1
        // 会把那个环境变量直接盖掉（`formData.planId || BASE_PLAN_ID` 里 1 是真值，
        // || 短路不到后面），导致测试/生产传的其实一直是这个 1，是个之前遗留的 bug
        planPrice: 4900,
      });
      this.close();
      this.$router.push("/appointment");
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/css/variables.scss";

.booking-mask {
  position: fixed;
  inset: 0;
  z-index: 320;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.5);
}

.booking-panel {
  width: 100%;
  max-width: 512px; // $content-max-width(560) - 24*2 的弹窗版本，跟别处内容宽度同一套逻辑
  max-height: 90vh;
  overflow-y: auto;
  box-sizing: border-box;
  padding: 24px;
  border-radius: 20px;
  background: $color-white;
}

.panel-header {
  position: relative;
  padding-right: 40px; // 给右上角关闭按钮让位置，标题文字不会被压到
}

.title-en {
  margin: 0;
  font-family: $font-logo;
  font-weight: 800;
  font-size: 26px;
  color: $color-primary;
}

// display:inline-block 让这个元素的宽度收缩到刚好包住文字，
// border-bottom 画出来的线也就正好跟文字等宽了，不用另外量线该多长
.title-cn {
  display: inline-block;

  padding-bottom: 10px;
  font-size: 15px;
  color: $color-ink;
  border-bottom: 1px solid $color-primary;
   font-weight: 800;
}

.close-btn {
  position: absolute;
  top: 0;
  right: 0;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: $color-primary;
  color: $color-white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
}

.field-group {
  position: relative; // 展开面板 position:absolute 挂靠在这个上面，不占文档流的高度
  margin-top: 10px;

  label {
    display: block;
    margin-bottom: 10px;
    font-size: 14px;
    color: $color-ink;
  }
}

.field {
  height: 44px;
  display: flex;
  align-items: center;
  border: 1px solid $color-border;
  border-radius: 22px;
  padding: 0 18px;
  box-sizing: border-box;

  input,
  select {
    flex: 1;
    min-width: 0;
    width: 100%;
    height: 100%;
    border: none;
    background: none;
    font-size: 14px;
    font-family: $font-body;
    color: $color-ink;

    &:focus {
      outline: none;
    }
  }
}

.toggle-field {
  position: relative;
  cursor: pointer;
  user-select: none;

  .field-display {
    flex: 1;
    font-size: 14px;
    color: $color-gray-light;
  }

  .chevron {
    flex-shrink: 0;
    color: $color-gray;
    transition: transform 0.15s ease;
  }

  // 展开的那个字段自己的箭头转向朝上，跟原型里"只有当前展开这个是 ^，其余都是 v"一致
  &.open .chevron {
    transform: rotate(180deg);
  }
}

// 三个面板共用：绝对定位悬浮在字段下方，不再挤占文档流的高度，
// 展开/收起不会把下面的字段、套餐框、按钮顶下去或弹回来
%floating-panel {
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  z-index: 6;
  margin-top: 10px;
  box-sizing: border-box;
  background: $color-white;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

// 日历面板
.calendar-panel {
  @extend %floating-panel;
  padding: 20px;
  border: 1px solid $color-border;
  border-radius: 20px;
}

.calendar-head {
  display: flex;
  align-items: center;
  justify-content: space-between;

  .cal-month {
    font-size: 15px;
    color: $color-ink;
  }

  .cal-nav {
    display: flex;
    gap: 14px;

    button {
      border: none;
      background: none;
      padding: 0;
      color: $color-gray;
      cursor: pointer;
      display: flex;
    }
  }
}

.cal-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-top: 22px;
  text-align: center;
  font-size: 13px;
  color: #4a4a4a;
}

.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  row-gap: 14px;
  margin-top: 18px;
}

.cal-day {
  width: 32px;
  height: 32px;
  margin: 0 auto;
  border: none;
  border-radius: 50%;
  background: none;
  font-size: 14px;
  font-family: $font-body;
  color: $color-primary; // 今天及以后可选的日期，原型里统一是主题绿
  cursor: pointer;

  &.past {
    color: $color-gray-light; // 今天之前的日期，灰掉且不能点
    cursor: not-allowed;
  }

  &.selected {
    background: $color-primary;
    color: $color-white;
  }
}

// 时间选择：4 列胶囊按钮
.time-panel {
  @extend %floating-panel;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  padding: 20px;
  border: 1px solid $color-border;
  border-radius: 20px;
}

.time-pill {
  padding: 3px 4px;
  border: 1px solid $color-border;
  border-radius: $radius-pill;
  background: #f7f7f7;
  color: $color-ink;
  font-size: 14px;
  font-family: $font-body;
  cursor: pointer;

  small {
    font-size: 10px;
    color: $color-gray;
  }

  &.selected {
    border-color: $color-primary;
    background: $color-primary;
    color: $color-white;

    small {
      color: $color-white;
    }
  }
}

// 人数选择：跟 header 语言下拉是同一款简单列表样式。
// 原来只到 4 人，一屏放得下不用滚动；现在最多支持 20 人，列表变长了，
// 限个高度、超出的部分滚动，不然面板会长到把下面的套餐区块都顶下去
.people-panel {
  @extend %floating-panel;
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid $color-border;
  border-radius: 20px;
}

.people-item {
  padding: 16px 20px;
  font-size: 15px;
  color: $color-ink;
  cursor: pointer;
  border-bottom: 1px solid $color-border;

  &:last-child {
    border-bottom: none;
  }

  &.selected {
    color: $color-primary-dark;
    font-weight: 700;
  }
}

.plan-box {
  position: relative;
  margin-top: 26px;
  padding: 20px;
  border: 1px solid $color-primary;
  border-radius: 16px;
  box-sizing: border-box;
}

// 徽标压在盒子右上角一半在外面，跟原型实测的位置一致
.plan-badge {
  position: absolute;
  top: -16px;
  right: -12px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: $color-primary;
  color: $color-white;
  display: flex;
  flex-direction: column; // "套餐"在上、"建议"在下，不是挤在一行里自动换行
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 14px;
  line-height: 1.3;
  // font-weight: 700;
}

.plan-head {
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
}

.plan-name {
  font-family: $font-heading-cn;
  font-size: 19px;
  color: $color-ink;
}

.plan-price {
  font-size: 20px;
  font-weight: 700;
  color: $color-primary;

  small {
    font-size: 12px;
    font-weight: 400;
  }
}

.plan-desc {
  margin: 14px 0 0;
  font-size: 13px;
  line-height: 1.8;
  color: $color-gray-light;
}

.plan-upsell {
  margin: 10px 0 0;
  font-size: 13px;
  line-height: 1.8;
  color: $color-primary;
}

.btn-book {
  display: block;
  width: 100%;
  margin-top: 24px;
  border: none;
  border-radius: $radius-pill;
  padding: 15px;
  background: $color-primary;
  color: $color-white;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 1px;
  cursor: pointer;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
